import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SearchIcon from "@mui/icons-material/Search";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  listMyAppointmentsPatient,
  normalizeAppointmentList,
  PATIENT_APPTS_QUERY_KEY,
} from "@/api/appointmentApi";
import { browserTimeZone } from "@/lib/clinicTime";
import { formatSlotStart } from "@/lib/appointmentTimeFormat";
import { appointmentBucket } from "@/lib/appointmentLifecycle";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentStatusBadge } from "@/components/appointment/AppointmentStatusBadge";
import { getPatientProfile } from "@/api/userApi";

export default function PatientDashboard() {
  const { fullName, username } = useAuth();
  const { data: rawAppts } = useQuery({
    queryKey: PATIENT_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsPatient,
  });
  const { data: profile } = useQuery({
    queryKey: ["patient-profile"],
    queryFn: getPatientProfile,
  });
  const appts = useMemo(
    () => normalizeAppointmentList(rawAppts),
    [rawAppts],
  );
  const profileIncomplete = useMemo(() => {
    if (!profile) return false;
    return !profile.phone?.trim() || !profile.dateOfBirth || !profile.gender;
  }, [profile]);

  const upcoming = appts.filter((a) => appointmentBucket(a) === "UPCOMING");
  const past = appts.filter((a) => appointmentBucket(a) === "PAST");

  return (
    <Box>
      <PageHeader
        title={`Welcome${fullName || username ? `, ${fullName || username}` : ""} 👋`}
        subtitle={`Here's what's next for your care. Times use your browser zone (${browserTimeZone()}).`}
        actions={
          <Button
            component={RouterLink}
            to="/doctors"
            variant="contained"
            startIcon={<SearchIcon />}
          >
            Find a doctor
          </Button>
        }
      />

      {profileIncomplete && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              component={RouterLink}
              to="/patient/profile"
              color="warning"
              variant="contained"
              size="small"
              sx={{ fontWeight: 800 }}
            >
              Complete profile
            </Button>
          }
        >
          Please complete your profile, especially your mobile number and health details.
          This helps us send appointment reminders and helps doctors assess you better.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <StatCard
          icon={<EventAvailableIcon />}
          label="Upcoming"
          value={upcoming.length}
        />
        <StatCard label="Completed" value={past.length} />
        <StatCard
          icon={<PaymentsIcon />}
          label="This month"
          value="—"
          sub="Open payments for full history"
        />
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Upcoming appointments
      </Typography>
      {upcoming.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>
            No upcoming appointments.
          </Typography>
          <Button
            component={RouterLink}
            to="/doctors"
            variant="contained"
            startIcon={<SearchIcon />}
          >
            Book your first consult
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {upcoming.map((a) => (
            <Paper key={a.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography fontWeight={700}>
                    {a.doctorName ?? "Doctor"}{" "}
                    {a.doctorSpecialization && (
                      <Typography
                        component="span"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        · {a.doctorSpecialization}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatSlotStart(a.slotStart, a.timeZone)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AppointmentStatusBadge status={a.status} />
                  <Button
                    component={RouterLink}
                    to={`/patient/appointments/${a.id}`}
                    variant="outlined"
                    size="small"
                  >
                    View
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Grid item xs={12} sm={4}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            {icon && (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {icon}
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {value}
              </Typography>
              {sub && (
                <Typography variant="caption" color="text.secondary">
                  {sub}
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
